import { ChevronLeft, ChevronRight, Link2, Plus } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { formatEuro } from "../gulaConstants";
import { MoneyTabs } from "../money/MoneyTabs";
import { useCostSettings } from "../money/useMoneyData";
import { useAgents } from "../useAgents";
import { AdjustmentDialog, MaintenanceLinksDialog } from "./CommissionDialogs";
import {
  KIND_LABEL,
  STATUS_LABEL,
  lineLabel,
  monthLabel,
  monthOf,
  shiftMonth,
  totalsBySetter,
  type Commission,
  type CommissionStatus,
  type SetterTotals,
} from "./commissions";
import { setCommissionStatus, useCommissions } from "./useCommissions";

const STATUS_TONE: Record<CommissionStatus, string> = {
  pending:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  approved: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
  paid: "bg-lime-100 text-lime-900 dark:bg-lime-900/40 dark:text-lime-100",
  void: "bg-muted text-muted-foreground line-through",
};

const shortDay = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

const Summary = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="rounded-lg border bg-card p-4">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    {hint ? (
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    ) : null}
  </div>
);

const Line = ({
  row,
  busy,
  onStatus,
}: {
  row: Commission;
  busy: boolean;
  onStatus: (row: Commission, status: CommissionStatus) => void;
}) => (
  <tr className="border-t">
    <td className="w-16 whitespace-nowrap py-2 pr-3 text-muted-foreground">
      {shortDay(row.day)}
    </td>
    <td className="py-2 pr-3">
      <div className="flex flex-col">
        {row.client_id ? (
          <Link
            to={`/clientes?cliente=${row.client_id}`}
            className="hover:underline"
          >
            {lineLabel(row)}
          </Link>
        ) : (
          <span>{lineLabel(row)}</span>
        )}
        {!row.qualified && row.reason && row.status === "pending" ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">
            {row.reason}: aprueba o anula
          </span>
        ) : null}
        {row.note && row.kind !== "adjustment" ? (
          <span className="text-xs text-muted-foreground">{row.note}</span>
        ) : null}
      </div>
    </td>
    <td className="w-32 py-2 pr-3 text-muted-foreground">
      {KIND_LABEL[row.kind]}
    </td>
    <td
      className={cn(
        "w-24 py-2 pr-3 text-right tabular-nums",
        row.status === "void" && "text-muted-foreground line-through",
      )}
    >
      {formatEuro(Number(row.amount))}
    </td>
    <td className="w-28 py-2 pr-3">
      <span
        className={cn(
          "rounded-full px-2 py-0.5 text-xs font-medium",
          STATUS_TONE[row.status],
        )}
      >
        {STATUS_LABEL[row.status]}
      </span>
    </td>
    <td className="w-56 py-2 text-right">
      <div className="flex justify-end gap-1">
        {row.status === "pending" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onStatus(row, "approved")}
          >
            Aprobar
          </Button>
        ) : null}
        {row.status === "pending" || row.status === "approved" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onStatus(row, "paid")}
          >
            Pagada
          </Button>
        ) : null}
        {row.status !== "void" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onStatus(row, "void")}
          >
            Anular
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onStatus(row, "pending")}
          >
            Reabrir
          </Button>
        )}
      </div>
    </td>
  </tr>
);

const SetterBlock = ({
  block,
  busy,
  onStatus,
  onPayAll,
}: {
  block: SetterTotals;
  busy: boolean;
  onStatus: (row: Commission, status: CommissionStatus) => void;
  onPayAll: (block: SetterTotals) => void;
}) => (
  <section className="rounded-lg border bg-card">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
      <div className="flex items-baseline gap-3">
        <h2 className="text-lg font-semibold">{block.name}</h2>
        <span className="text-sm text-muted-foreground">
          {block.lines.length} {block.lines.length === 1 ? "línea" : "líneas"}
          {block.doubtful ? ` · ${block.doubtful} por decidir` : ""}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          Pagado {formatEuro(block.paid)}
        </span>
        <Badge variant={block.due ? "default" : "secondary"}>
          Por pagar {formatEuro(block.due)}
        </Badge>
        {block.due > 0 ? (
          <Button size="sm" disabled={busy} onClick={() => onPayAll(block)}>
            Marcar todo pagado
          </Button>
        ) : null}
      </div>
    </div>
    <div className="overflow-x-auto px-4">
      <table className="w-full text-sm">
        <tbody>
          {block.lines.map((row) => (
            <Line key={row.id} row={row} busy={busy} onStatus={onStatus} />
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

/**
 * Dinero → Comisiones: the monthly ledger of what each setter has earned,
 * approved, paid or voided line by line.
 */
export const CommissionsPage = () => {
  const notify = useNotify();
  const { agents } = useAgents();
  const { data: settings, refetch: refetchSettings } = useCostSettings();
  const [month, setMonth] = useState(() => monthOf(new Date()));
  const { data: rows = [], isPending, refetch } = useCommissions(month);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [links, setLinks] = useState(false);
  const blocks = totalsBySetter(rows);
  const due = blocks.reduce((sum, block) => sum + block.due, 0);
  const paid = blocks.reduce((sum, block) => sum + block.paid, 0);
  const doubtful = blocks.reduce((sum, block) => sum + block.doubtful, 0);
  const isCurrent = month === monthOf(new Date());

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      refetch();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };
  const onStatus = (row: Commission, status: CommissionStatus) =>
    run(() => setCommissionStatus(row.id, status));
  const onPayAll = (block: SetterTotals) =>
    run(async () => {
      const toPay = block.lines.filter(
        (row) =>
          row.status === "approved" ||
          (row.status === "pending" && row.qualified),
      );
      for (const row of toPay) await setCommissionStatus(row.id, "paid");
      notify(`${block.name}: ${formatEuro(block.due)} marcados como pagados`, {
        type: "success",
      });
    });

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Comisiones</h1>
          <p className="text-sm text-muted-foreground">
            20 € por venta (lead suyo y hablado en los 30 días anteriores) y 5 €
            por mantenimiento cerrado en la llamada de bienvenida. Las ventas
            cerradas por el equipo no suman.
          </p>
        </div>
        <MoneyTabs />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mes anterior"
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-40 text-center text-lg font-medium">
            {monthLabel(month)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mes siguiente"
            disabled={isCurrent}
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLinks(true)}>
            <Link2 className="h-4 w-4" />
            Enlaces de pago
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Añadir ajuste
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Summary
          label="Por pagar"
          value={formatEuro(due)}
          hint="Aprobadas, más las pendientes que cumplen la regla"
        />
        <Summary label="Pagado este mes" value={formatEuro(paid)} />
        <Summary
          label="Por decidir"
          value={String(doubtful)}
          hint="Ventas sin llamada hablada o cerradas por el equipo"
        />
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : blocks.length ? (
        blocks.map((block) => (
          <SetterBlock
            key={block.sales_id}
            block={block}
            busy={busy}
            onStatus={onStatus}
            onPayAll={onPayAll}
          />
        ))
      ) : (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          Sin comisiones este mes. Cada venta con vendedor crea su línea sola.
        </p>
      )}

      <AdjustmentDialog
        open={adding}
        onOpenChange={setAdding}
        agents={agents}
        onSaved={refetch}
      />
      <MaintenanceLinksDialog
        open={links}
        onOpenChange={setLinks}
        settings={settings}
        onSaved={refetchSettings}
      />
    </div>
  );
};

CommissionsPage.path = "/dinero/comisiones";
